import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Autocomplete, Button, TextField, Tooltip, Typography } from "@mui/material";
import { toast } from "react-toastify";
import CustomModal from "../../../common-utilities/core/modal";
import MuiTable from "../../../common-utilities/core/mui-table";
import { HTTP_METHODS, invokeApi } from "../../utils/http-service";
import { FINANCE_V2_HOSTNAME } from "../../../common-utilities/utils/end-points";
import { FINANCE_REST_URLS } from "../../../common-utilities/utils/end-points/finance-v2";
import { PO_TYPES } from "./helper";
import FinancialYearDropdown from "../home/financial-year-dropdown";
import { isStoreRole, canConvertPRToPO } from "../../utils/common-utils";

const toPrType = (poTypeValue) => (poTypeValue === "opex_po" ? "OPEX" : "CAPEX");
const OPEX_PO_TYPE = PO_TYPES.find((t) => t.value === "opex_po");

const getSelectablePRColumns = (onSelect) => [
  {
    label: "PR Number",
    id: "prNumber",
    render: (row) => row.prNumber || row.id,
  },
  {
    label: "Site",
    id: "siteName",
    render: (row) =>
      row.siteName
        ? `${row.siteCode ? row.siteCode + " - " : ""}${row.siteName}`
        : "-",
  },
  {
    label: "Total Est. Amount",
    id: "totalEstimatedGrossAmount",
    render: (row) =>
      row.totalEstimatedGrossAmount != null
        ? `₹${Number(row.totalEstimatedGrossAmount).toLocaleString("en-IN", {
            minimumFractionDigits: 2,
          })}`
        : "-",
  },
  {
    label: "Action",
    id: "actions",
    // Same rule as pr-details' Convert to PO/Bulk PO gate (canConvertPRToPO) -- this list is
    // only pre-filtered by department/status/financialYear/prType, not by category, so a
    // regular department user's own Procurement Budget/Procurement Budget Opex PRs still show
    // up here and need to be blocked from selection individually, same as the backend would
    // reject them (see convertToPO in purchase-request.controller.js).
    render: (row) =>
      canConvertPRToPO(row) ? (
        <Button variant="outlined" size="small" onClick={() => onSelect(row)}>
          Select
        </Button>
      ) : (
        <Tooltip title="Only Procurement & Supply Chain can convert this PR into a PO">
          <span>
            <Button variant="outlined" size="small" disabled>
              Select
            </Button>
          </span>
        </Tooltip>
      ),
  },
];

const CreatePOFromPRModal = ({ onClose }) => {
  const navigate = useNavigate();
  const storeRole = isStoreRole();
  const [financialYear, setFinancialYear] = useState(null);
  const [poType, setPoType] = useState(storeRole ? OPEX_PO_TYPE : null);
  const [filters, setFilters] = useState({
    page: 1,
    limit: 10,
    sortBy: "-createdAt",
  });
  const [prResponse, setPrResponse] = useState({});

  const fyFilters = { financialYear };
  const setFyFilters = (updater) => {
    const next = typeof updater === "function" ? updater(fyFilters) : updater;
    const newFY = next?.financialYear ?? null;
    if (newFY !== financialYear) {
      setFinancialYear(newFY);
      // Store role: PO Type is always OPEX, never reset to null.
      setPoType(storeRole ? OPEX_PO_TYPE : null);
      setFilters({ page: 1, limit: 10, sortBy: "-createdAt" });
      setPrResponse({});
    }
  };

  const handlePoTypeChange = (val) => {
    setPoType(val);
    setFilters({ page: 1, limit: 10, sortBy: "-createdAt" });
    setPrResponse({});
  };

  useEffect(() => {
    if (financialYear && poType?.value) {
      invokeApi(
        HTTP_METHODS.GET,
        `${FINANCE_V2_HOSTNAME}${FINANCE_REST_URLS.PURCHASE_REQUISITION}`,
        null,
        {
          status: "approved",
          financialYear,
          prType: toPrType(poType.value),
          page: filters.page,
          limit: filters.limit,
          sortBy: filters.sortBy,
        }
      )
        .then((response) => {
          if (response.message) {
            setPrResponse({});
          } else {
            setPrResponse(response);
          }
        })
        .catch(() => setPrResponse({}));
    } else {
      setPrResponse({});
    }
  }, [financialYear, poType, filters.page]);

  const handleSelectPR = (pr) => {
    // Belt-and-braces: the row's own Select button is already hidden/disabled for a PR this
    // viewer can't convert (see getSelectablePRColumns), but guard the actual navigation too
    // in case this is ever invoked some other way.
    if (!canConvertPRToPO(pr)) {
      toast.error(
        "You do not have permission to convert this Purchase Requisition into a Purchase Order"
      );
      return;
    }
    const budgetId = pr.budget?.id || pr.budget;
    if (!budgetId) {
      toast.error(
        "This PR has no linked budget and cannot be used to create a PO."
      );
      return;
    }
    navigate(`purchase-order/create/${budgetId}?prId=${pr.id || pr._id}`);
  };

  const results = prResponse.results || [];

  return (
    <CustomModal
      title="Create Purchase Order from PR"
      disabledOutsideClick={true}
      onClose={() => {
        setFinancialYear(null);
        setPoType(storeRole ? OPEX_PO_TYPE : null);
        setFilters({ page: 1, limit: 10, sortBy: "-createdAt" });
        setPrResponse({});
        onClose();
      }}
    >
      <div className="finance-v2-po-modal-content">
        <FinancialYearDropdown
          filters={fyFilters}
          setFilters={setFyFilters}
          isFullWidth={true}
        />
        {/* STORE role: PO Type is always OPEX -- the selector is hidden, not just disabled. */}
        {!storeRole && (
          <Autocomplete
            value={poType}
            onChange={(e, val) => handlePoTypeChange(val)}
            renderInput={(params) => (
              <TextField {...params} size="small" label="PO Type *" />
            )}
            options={PO_TYPES}
            isOptionEqualToValue={(opt, val) => opt.value === val?.value}
          />
        )}

        {!financialYear || !poType?.value ? (
          <Typography variant="body2" color="text.secondary">
            Select Financial Year and PO Type to view eligible PRs.
          </Typography>
        ) : results.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No approved, open PRs found for the selected Financial Year and PO
            Type.
          </Typography>
        ) : (
          <MuiTable
            columnsList={getSelectablePRColumns(handleSelectPR)}
            dataList={results}
            filters={filters}
            pageCount={prResponse.totalPages}
            onChange={(page) => setFilters((prev) => ({ ...prev, page }))}
          />
        )}
      </div>
    </CustomModal>
  );
};

export default CreatePOFromPRModal;
